import { AddressInput } from '@app/app/_components/AddressInput/AddressInput';
import { Heading } from '@app/app/_components/Heading/Heading';
import { CheckboxInput } from '@app/app/_components/Input/CheckboxInput';
import { Input } from '@app/app/_components/Input/Input';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseTransportInsuranceResult } from '@app/service/eshop/transport/actions/purchaseTransportInsurance';
import { useTranslations } from 'next-intl';
import { useOwnerFormFields } from '../setups/useOwnerFormFields';
import { CountryCode } from '@app/domain/model/CountryCode';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { preventPasting } from '@app/lib/event-handlers/preventPasting';
import { preventDropping } from '@app/lib/event-handlers/preventDropping';

interface Props {
    formState: FormState<PurchaseTransportInsuranceResult>;
    ownerFormFields: ReturnType<typeof useOwnerFormFields>;
    isCompany: boolean;
}

export function VehicleOwnerStep({ formState, ownerFormFields, isCompany }: Props) {
    const t = useTranslations();

    const { ownerFormData, handleInputField, handlePhoneNumberField, onChangeOwnerAddress, onChangePayerAddress } =
        ownerFormFields;

    return (
        <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
                <Heading level="h4">
                    {isCompany ? t('profile.companyInformation') : t('eshop.transport.personalInformation')}
                </Heading>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Input
                        name="ownerFirstName"
                        label={isCompany ? t('profile.companyNameLabel') : t('eshop.transport.firstNameLabel')}
                        placeholder={
                            isCompany ? t('profile.companyNamePlaceholder') : t('eshop.transport.firstNamePlaceholder')
                        }
                        value={ownerFormData.ownerFirstName}
                        onChange={handleInputField('ownerFirstName')}
                        error={formState.violations?.ownerFirstName}
                    />
                    {!isCompany && (
                        <Input
                            name="ownerLastName"
                            label={t('eshop.transport.lastNameLabel')}
                            placeholder={t('eshop.transport.lastNamePlaceholder')}
                            value={ownerFormData.ownerLastName}
                            onChange={handleInputField('ownerLastName')}
                            error={formState.violations?.ownerLastName}
                        />
                    )}
                    <input type="hidden" name="ownerPersonalCode" value={ownerFormData.ownerPersonalCode} />
                    <ReadOnlyField
                        label={isCompany ? t('profile.companyCodeLabel') : t('eshop.transport.personalCodeLabel')}>
                        {ownerFormData.ownerPersonalCode}
                    </ReadOnlyField>
                </div>
            </div>
            <div className="flex flex-col gap-4">
                <Heading level="h4">{t('eshop.transport.contactInformation')}</Heading>
                <AddressInput
                    disableCountrySelect
                    defaultCountry={CountryCode.Lithuania}
                    countryOptions={[
                        {
                            value: 'lt',
                            label: t('auth.countries.lithuania'),
                        },
                        {
                            value: 'ee',
                            label: t('auth.countries.estonia'),
                        },
                        {
                            value: 'lv',
                            label: t('auth.countries.latvia'),
                        },
                    ]}
                    namePrefix="owner"
                    defaultValue={ownerFormData.ownerAddress ?? undefined}
                    violations={formState.violations}
                    onChange={onChangeOwnerAddress}
                />
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="md:col-span-2">
                    <input
                        type="hidden"
                        name="phoneNumber"
                        value={`+370${ownerFormData.phoneNumber.replace(/\D/g, '')}`}
                    />
                    <Input
                        label={t('eshop.transport.phoneNumberLabel')}
                        placeholder={t('eshop.transport.phoneNumberPlaceholder')}
                        value={ownerFormData.phoneNumber}
                        onChange={handlePhoneNumberField('phoneNumber')}
                        prefix="+370"
                        error={formState.violations?.phoneNumber}
                    />
                </div>
                <div>
                    <Input
                        className="md:col-start-1"
                        name="email"
                        label={t('eshop.transport.emailLabel')}
                        placeholder={t('eshop.transport.emailPlaceholder')}
                        description={t('eshop.transport.emailDescription')}
                        value={ownerFormData.email}
                        onChange={handleInputField('email')}
                        onPaste={preventPasting}
                        onDrop={preventDropping}
                        autoComplete="off"
                        error={formState.violations?.email}
                    />
                </div>
                <div>
                    <Input
                        name="emailConfirmation"
                        label={t('eshop.transport.confirmEmailL\abel')}
                        placeholder={t('eshop.transport.confirmEmailPlaceholder')}
                        description={t('eshop.transport.confirmEmailDescription')}
                        value={ownerFormData.emailConfirmation}
                        onChange={handleInputField('emailConfirmation')}
                        onPaste={preventPasting}
                        onDrop={preventDropping}
                        autoComplete="off"
                        error={formState.violations?.emailConfirmation}
                    />
                </div>
            </div>

            <CheckboxInput
                name="insuredByOwner"
                label={t('eshop.transport.insuredByOwnerHimself')}
                checked={ownerFormData.insuredByOwner}
                onChange={handleInputField('insuredByOwner')}
            />

            {!ownerFormData.insuredByOwner && (
                <>
                    <div className="flex flex-col gap-4">
                        <Heading level="h4">{t('eshop.transport.personalPayerInformation')}</Heading>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                            <Input
                                name="payerFirstName"
                                label={t('eshop.transport.firstNameLabel')}
                                placeholder={t('eshop.transport.firstNamePlaceholder')}
                                value={ownerFormData.payerFirstName}
                                onChange={handleInputField('payerFirstName')}
                                error={formState.violations?.payerFirstName}
                            />
                            <Input
                                name="payerLastName"
                                label={t('eshop.transport.lastNameLabel')}
                                placeholder={t('eshop.transport.lastNamePlaceholder')}
                                value={ownerFormData.payerLastName}
                                onChange={handleInputField('payerLastName')}
                                error={formState.violations?.payerLastName}
                            />
                            <Input
                                name="payerPersonalCode"
                                label={
                                    isCompany
                                        ? t('eshop.transport.personalCompanyCodePlaceholder')
                                        : t('eshop.transport.personalCodeLabel')
                                }
                                placeholder={
                                    isCompany
                                        ? t('eshop.transport.personalCompanyCodePlaceholder')
                                        : t('eshop.transport.personalCodePlaceholder')
                                }
                                value={ownerFormData.payerPersonalCode}
                                onChange={handleInputField('payerPersonalCode')}
                                error={formState.violations?.payerPersonalCode}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col gap-4">
                        <Heading level="h4">{t('eshop.transport.contactInformation')}</Heading>
                        <AddressInput
                            disableCountrySelect
                            defaultCountry={CountryCode.Lithuania}
                            countryOptions={[
                                {
                                    value: 'lt',
                                    label: t('auth.countries.lithuania'),
                                },
                                {
                                    value: 'ee',
                                    label: t('auth.countries.estonia'),
                                },
                                {
                                    value: 'lv',
                                    label: t('auth.countries.latvia'),
                                },
                            ]}
                            namePrefix="payer"
                            defaultValue={ownerFormData.payerAddress ?? undefined}
                            violations={formState.violations}
                            onChange={onChangePayerAddress}
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className="md:col-span-2">
                            <input
                                type="hidden"
                                name="payerPhoneNumber"
                                value={`+370${ownerFormData.payerPhoneNumber.replace(/\D/g, '')}`}
                            />
                            <Input
                                label={t('eshop.transport.phoneNumberLabel')}
                                placeholder={t('eshop.transport.phoneNumberPlaceholder')}
                                value={ownerFormData.payerPhoneNumber}
                                onChange={handlePhoneNumberField('payerPhoneNumber')}
                                prefix="+370"
                                error={formState.violations?.payerPhoneNumber}
                            />
                        </div>
                        <div>
                            <Input
                                className="md:col-start-1"
                                name="payerEmail"
                                label={t('eshop.transport.emailLabel')}
                                placeholder={t('eshop.transport.emailPlaceholder')}
                                description={t('eshop.transport.payerEmailDescription')}
                                value={ownerFormData.payerEmail}
                                onChange={handleInputField('payerEmail')}
                                error={formState.violations?.payerEmail}
                                onPaste={preventPasting}
                                onDrop={preventDropping}
                                autoComplete="off"
                            />
                        </div>
                        <div>
                            <Input
                                name="payerEmailConfirmation"
                                label={t('eshop.transport.confirmEmailL\abel')}
                                placeholder={t('eshop.transport.confirmEmailPlaceholder')}
                                description={t('eshop.transport.confirmEmailDescription')}
                                value={ownerFormData.payerEmailConfirmation}
                                onChange={handleInputField('payerEmailConfirmation')}
                                error={formState.violations?.payerEmailConfirmation}
                                onPaste={preventPasting}
                                onDrop={preventDropping}
                                autoComplete="off"
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
